import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Tabs,
  Button,
  Tag,
  Input,
  DatePicker,
  Space,
  Tooltip,
  Popconfirm,
  Form,
  Badge,
  Row,
  Col,
  Select,
  Divider,
  Table,
  Segmented,
  Empty,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, PrinterOutlined,
  LockOutlined, SafetyCertificateOutlined, ReloadOutlined, BookOutlined,
  UnorderedListOutlined, CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import CBModal from '../../components/CBModal';
import TableExportButton from '../../components/TableExportButton';
import {
  jourISO, registresATraiter, registresAffiches, actionDuRegistre,
} from './registresListe';
import PageHeader from '../../components/PageHeader';

import i18n from '../../i18n';
import { libellesTraduits } from '../../i18n/libelles';

const GREEN = '#1a6b3c';

const STATUT_COLOR = { OUVERT: 'green', CLOS: 'orange', VISE: 'blue' };
const STATUT_LABEL = libellesTraduits('courriers:registre.statuts');


const newLigne = () => ({
  _id: Math.random().toString(36).slice(2),
  dateCorrespondance: null,
  numeroCorrespondance: '',
  expediteur: '',
  objet: '',
  serviceDestinataire: '',
  saved: false,
  courrierTrackingId: null,
});

/* Colonnes de l'export, hors du composant : elles ne dependent ni du type de
   registre ni de l'etat. Elles vivaient dans `renderRegistre`, donc elles etaient
   reconstruites a chaque rendu des deux onglets - et inaccessibles au bouton
   flottant, qui est rendu une seule fois, hors des onglets. */
const colonnesExport = (t) => [
  { title: t('registre.colonnes.numero'), key: 'ordre', export: (_, __, idx) => (idx ?? 0) + 1, numeric: true },
  {
    title: t('registre.colonnes.dateCorrespondance'), dataIndex: 'dateCorrespondance', key: 'dc', format: 'date',
    export: (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '')
  },
  { title: t('registre.colonnes.numeroCorrespondance'), dataIndex: 'numeroCorrespondance', key: 'nc' },
  { title: t('registre.colonnes.expediteur'), dataIndex: 'expediteur', key: 'exp' },
  { title: t('registre.colonnes.objet'), dataIndex: 'objet', key: 'obj' },
  { title: t('registre.colonnes.serviceDestinataire'), dataIndex: 'serviceDestinataire', key: 'sd' },
];

export default function RegistreJournalier() {
  const { t } = useTranslation('courriers');
  const [registreArrivee, setRegistreArrivee] = useState(null);
  const [registreDepart, setRegistreDepart] = useState(null);
  const [lignesArrivee, setLignesArrivee] = useState([]);
  const [lignesDepart, setLignesDepart] = useState([]);
  const [activeTab, setActiveTab] = useState('ARRIVEE');
  const [loading, setLoading] = useState(false);

  // Visa modal
  const [visaModal, setVisaModal] = useState(null); // 'ARRIVEE' | 'DEPART'
  const [visaForm] = Form.useForm();

  const today = dayjs().format('YYYY-MM-DD');

  /* LA DATE CONSULTEE, ET NON PLUS « AUJOURD'HUI » EN DUR. L'ecran ne chargeait que
     le registre du jour, sans selecteur ni liste : les registres des jours passes
     etaient inatteignables, donc impossibles a cloturer et a viser. Douze
     attendaient, tous au statut Ouvert. */
  const [dateSelectionnee, setDateSelectionnee] = useState(today);
  const consulteAujourdhui = dateSelectionnee === today;

  // La liste de tous les registres, pour l'onglet qui sert a les retrouver.
  const [tousRegistres, setTousRegistres] = useState([]);
  const [chargementListe, setChargementListe] = useState(false);
  const [filtreStatut, setFiltreStatut] = useState('');

  // ─── Charger le registre de la date consultée ─────────────
  const loadRegistre = useCallback(async (type) => {
    const poser = (reg, lignes) => {
      if (type === 'ARRIVEE') { setRegistreArrivee(reg); setLignesArrivee(lignes); }
      else { setRegistreDepart(reg); setLignesDepart(lignes); }
    };
    try {
      const res = await axiosInstance.get(
        `/v1/registres/du-jour?date=${dateSelectionnee}&type=${type}`);
      const reg = res.data?.data;
      poser(reg, mapCourriersToLignes(reg?.courriers || []));
    } catch (e) {
      if (e.response?.status === 404) {
        /* ON N'OUVRE UN REGISTRE QUE POUR AUJOURD'HUI. Le creer parce qu'on a
           consulte une date passee fabriquerait une piece dans la main courante :
           un registre vide, date d'hier, ouvert aujourd'hui. Sur une date passee
           sans registre, on le dit et on n'ecrit rien. */
        if (!consulteAujourdhui) { poser(null, []); return; }
        try {
          const created = await axiosInstance.post('/v1/registres', {
            dateJour: dateSelectionnee,
            typeRegistre: type,
          });
          poser(created.data?.data, []);
        } catch { toast.error(i18n.t('courriers:registre.creationImpossible', { type })); }
      } else {
        // Une erreur non traitee laissait a l'ecran le registre de la date PRECEDENTE.
        poser(null, []);
      }
    }
  }, [dateSelectionnee, consulteAujourdhui]);

  /* La liste complete, pour retrouver un registre sans deviner sa date.
     `GET /v1/registres` existait cote serveur et n'etait appele par personne. */
  const chargerTousRegistres = useCallback(async () => {
    setChargementListe(true);
    try {
      const res = await axiosInstance.get('/v1/registres');
      setTousRegistres(res.data?.data || []);
    } catch { /* message du serveur affiche par l'intercepteur */ }
    finally { setChargementListe(false); }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadRegistre('ARRIVEE'), loadRegistre('DEPART')]).finally(() => setLoading(false));
  }, [loadRegistre]);

  useEffect(() => { chargerTousRegistres(); }, [chargerTousRegistres]);

  /* Ouvrir un registre depuis la liste : la date ET le type, sinon on retomberait
     sur le volet d'un type qui n'a pas de registre ce jour-la. */
  const ouvrirRegistre = (reg) => {
    setDateSelectionnee(jourISO(reg.dateJour));
    setActiveTab(reg.typeRegistre);
  };

  const mapCourriersToLignes = (courriers) =>
    courriers.map(c => ({
      _id: c.courrierTrackingId,
      courrierTrackingId: c.courrierTrackingId,
      dateCorrespondance: c.dateCourrier ? dayjs(c.dateCourrier) : null,
      numeroCorrespondance: c.referenceCourrier || '',
      expediteur: c.expediteur || '',
      objet: c.objet || '',
      serviceDestinataire: c.serviceDestinataireInterne || '',
      saved: true,
    }));

  // ─── Getters / setters par type ──────────────────────────
  const getLignes = (type) => type === 'ARRIVEE' ? lignesArrivee : lignesDepart;
  const setLignes = (type, val) =>
    type === 'ARRIVEE' ? setLignesArrivee(val) : setLignesDepart(val);
  const getRegistre = (type) => type === 'ARRIVEE' ? registreArrivee : registreDepart;

  // ─── Manipulation lignes ─────────────────────────────────
  const addLigne = (type) => {
    const current = getLignes(type);
    setLignes(type, [...current, newLigne()]);
  };

  const dupLigne = (type, idx) => {
    const current = getLignes(type);
    const src = current[idx];
    const next = [...current];
    next.splice(idx + 1, 0, { ...src, _id: Math.random().toString(36).slice(2), saved: false, courrierTrackingId: null });
    setLignes(type, next);
  };

  const removeLigne = (type, id) =>
    setLignes(type, getLignes(type).filter(l => l._id !== id));

  const updateLigne = (type, id, patch) =>
    setLignes(type, getLignes(type).map(l => l._id === id ? { ...l, ...patch } : l));

  // ─── Enregistrer une ligne (POST courrier) ───────────────
  const saveLigne = async (type, ligne) => {
    const registre = getRegistre(type);
    if (!registre) { toast.warn(t('registre.indisponible')); return; }
    if (!ligne.expediteur && !ligne.objet) { toast.warn(t('registre.ligneVide')); return; }
    try {
      await axiosInstance.post('/v1/courriers', {
        typeCourrier: type === 'ARRIVEE' ? 'ENTRANT' : 'SORTANT',
        nature: 'AUTRE',
        expediteur: ligne.expediteur || '-',
        destinataire: 'BNCB-TG',
        objet: ligne.objet || '-',
        dateCourrier: ligne.dateCorrespondance ? ligne.dateCorrespondance.format('YYYY-MM-DD') : dateSelectionnee,
        referenceCourrier: ligne.numeroCorrespondance || null,
        canal: 'COURRIER_POSTAL',
        serviceDestinataireInterne: ligne.serviceDestinataire || null,
        registreJourTrackingId: registre.registreTrackingId,
      });
      toast.success(t('registre.enregistre'));
      await loadRegistre(type);
    } catch (e) { toast.error(e.response?.data?.message || t('registre.erreurEnregistrement')); }
  };

  // ─── Clôturer ────────────────────────────────────────────
  const cloturer = async (type) => {
    const reg = getRegistre(type);
    if (!reg) return;
    try {
      await axiosInstance.patch(`/v1/registres/${reg.registreTrackingId}/cloturer`);
      toast.success(t('registre.cloture', { type }));
      await Promise.all([loadRegistre(type), chargerTousRegistres()]);
    } catch (e) { toast.error(e.response?.data?.message || t('registre.erreurCloture')); }
  };

  // ─── Viser ───────────────────────────────────────────────
  const viser = async (vals) => {
    const reg = getRegistre(visaModal);
    if (!reg) return;
    try {
      await axiosInstance.patch(`/v1/registres/${reg.registreTrackingId}/viser`, {
        commentaireChef: vals.commentaireChef || '',
      });
      toast.success(t('registre.vise'));
      setVisaModal(null);
      visaForm.resetFields();
      await Promise.all([loadRegistre(visaModal), chargerTousRegistres()]);
    } catch (e) { toast.error(e.response?.data?.message || t('registre.erreurVisa')); }
  };

  // ─── Impression ──────────────────────────────────────────
  const imprimer = (type) => {
    const reg = getRegistre(type);
    const lignes = getLignes(type);
    // Registre interne : imprimé dans la langue de l'interface.
    const sens = type === 'ARRIVEE' ? 'arrivee' : 'depart';
    const todayLabel = dayjs().format('DD/MM/YYYY');
    const dateRegistre = reg?.dateJour ? dayjs(reg.dateJour).format('DD/MM/YYYY') : todayLabel;
    const statut = STATUT_LABEL[reg?.statut] || '-';

    const escape = (s) => String(s ?? '').replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

    const lignesRows = lignes.map((l, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${l.dateCorrespondance ? dayjs(l.dateCorrespondance).format('DD/MM/YYYY') : ''}</td>
        <td class="mono">${escape(l.numeroCorrespondance)}</td>
        <td>${escape(l.expediteur)}</td>
        <td>${escape(l.objet)}</td>
        <td>${escape(l.serviceDestinataire)}</td>
      </tr>
    `).join('');

    const lignesVides = Math.max(0, 10 - lignes.length);
    const videsRows = Array.from({ length: lignesVides }).map((_, i) => `
      <tr class="empty">
        <td class="c">${lignes.length + i + 1}</td>
        <td></td><td></td><td></td><td></td><td></td>
      </tr>
    `).join('');

    const html = `<!doctype html>
<html lang="${i18n.language}">
<head>
  <meta charset="utf-8" />
  <title>${escape(t(`registre.impression.fenetre.${sens}`, { date: dateRegistre }))}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #111; padding: 24px; margin: 0; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #1a6b3c; }
    .header .left { width: 30%; }
    .header .center { flex: 1; text-align: center; }
    .header .right { width: 25%; text-align: right; }
    .org-name { font-weight: 700; font-size: 13px; color: #1a6b3c; }
    .org-sub { font-size: 11px; color: #555; }
    .title { font-weight: 700; font-size: 16px; text-transform: uppercase; letter-spacing: 0.5px; }
    .subtitle { margin-top: 4px; font-size: 12px; }
    .meta { font-size: 11px; }
    .meta strong { color: #1a6b3c; }
    table.reg { width: 100%; border-collapse: collapse; margin-top: 8px; }
    table.reg th, table.reg td { border: 1px solid #555; padding: 6px 8px; vertical-align: top; }
    table.reg th { background: #e8f5ed; font-weight: 700; text-align: center; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; }
    table.reg td { font-size: 11px; }
    table.reg tr.empty td { height: 22px; color: #aaa; }
    .c { text-align: center; }
    .mono { font-family: 'Courier New', monospace; }
    .footer { margin-top: 48px; display: flex; justify-content: space-around; }
    .signature { width: 40%; text-align: center; padding-top: 56px; border-top: 1px solid #333; font-size: 11px; }
    .printed { margin-top: 24px; text-align: right; font-size: 10px; color: #777; font-style: italic; }
    @page { size: A4 landscape; margin: 14mm; }
    @media print {
      body { padding: 0; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="left">
      <div class="org-name">BNCB - TOGO</div>
      <div class="org-sub">${escape(t('registre.impression.organisme'))}</div>
      <div class="org-sub">${escape(t('registre.impression.carteBrune'))}</div>
    </div>
    <div class="center">
      <div class="title">${escape(t(`registre.impression.titre.${sens}`))}</div>
      <div class="subtitle">${escape(t('registre.impression.dateRegistre'))} <strong>${dateRegistre}</strong></div>
    </div>
    <div class="right meta">
      ${escape(t('registre.impression.statut'))} <strong>${escape(statut)}</strong><br/>
      ${escape(t('registre.impression.totalEntrees'))} <strong>${lignes.length}</strong>
    </div>
  </div>

  <table class="reg">
    <thead>
      <tr>
        <th style="width:36px">${escape(t('registre.colonnes.numero'))}</th>
        <th style="width:100px">${escape(t('registre.colonnes.dateCorrespondance'))}</th>
        <th style="width:130px">${escape(t('registre.colonnes.numeroCorrespondance'))}</th>
        <th style="width:180px">${escape(t('registre.colonnes.expediteur'))}</th>
        <th>${escape(t('registre.colonnes.objet'))}</th>
        <th style="width:160px">${escape(t('registre.colonnes.serviceDestinataire'))}</th>
      </tr>
    </thead>
    <tbody>
      ${lignesRows}
      ${videsRows}
    </tbody>
  </table>

  <div class="footer">
    <div class="signature">${escape(t('registre.impression.signatureSecretaire'))}</div>
    <div class="signature">${escape(t('registre.impression.visaChef'))}</div>
  </div>

  <div class="printed">${escape(t('registre.impression.imprimeLe', { date: todayLabel }))}</div>

  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.print(); }, 150);
      window.addEventListener('afterprint', function () { window.close(); });
    });
  </script>
</body>
</html>`;

    const w = window.open('', '_blank', 'width=1100,height=800');
    if (!w) {
      toast.error(t('registre.popupBloquee'));
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // ─────────────────────────────────────────────────────────
  // Render d'un onglet registre
  // ─────────────────────────────────────────────────────────
  /* Les lignes de l'onglet ACTIF, pour le bouton flottant unique. `_ordre` est
     ajoute ici comme il l'etait dans le rendu : l'export numerote ses lignes. */
  const estVueListe = activeTab === 'TOUS';

  const aTraiter = registresATraiter(tousRegistres).length;
  const lignesExportables = estVueListe
    ? []
    : getLignes(activeTab).map((l, i) => ({ ...l, _ordre: i + 1 }));

  /* ── La liste des registres ──────────────────────────────────────────────
     TRIEE DU PLUS RECENT AU PLUS ANCIEN, et filtrable par statut. Le tri
     chronologique brut aurait mis en tete le registre de juillet : ce qui attend
     une action est presque toujours recent.

     Le filtre nomme l'ACTION attendue, pas le statut : « a cloturer » plutot
     qu'« Ouvert ». C'est la question que se pose celui qui vient ici. */
  const FILTRES_LISTE = [
    { value: '', label: t('registre.liste.filtres.tous') },
    { value: 'OUVERT', label: t('registre.liste.filtres.aCloturer') },
    { value: 'CLOS', label: t('registre.liste.filtres.aViser') },
    { value: 'VISE', label: t('registre.liste.filtres.vises') },
  ];

  const listeAffichee = registresAffiches(tousRegistres, filtreStatut);

  const COLONNES_LISTE = [
    {
      title: t('registre.liste.colonnes.date'), dataIndex: 'dateJour', key: 'date', width: 130,
      render: (v) => (
        <span>
          <strong>{v ? dayjs(v).format('DD/MM/YYYY') : '-'}</strong>
          {jourISO(v) === today && <Tag color={GREEN} style={{ marginLeft: 8 }}>{t('registre.liste.aujourdhui')}</Tag>}
        </span>
      ),
    },
    {
      title: t('registre.liste.colonnes.type'), dataIndex: 'typeRegistre', key: 'type', width: 110,
      render: (v) => (v === 'ARRIVEE' ? t('registre.types.arrivee') : t('registre.types.depart')),
    },
    {
      title: t('registre.liste.colonnes.statut'), dataIndex: 'statut', key: 'statut', width: 120,
      render: (v) => <Tag color={STATUT_COLOR[v] || 'default'}>{STATUT_LABEL[v] || '-'}</Tag>,
    },
    {
      title: t('registre.liste.colonnes.courriers'), dataIndex: 'nombreCourriers', key: 'nb', width: 100, align: 'center',
      render: (v) => v ?? 0,
    },
    { title: t('registre.liste.colonnes.secretaire'), dataIndex: 'secretaireNomComplet', key: 'sec', render: (v) => v || '-' },
    {
      title: t('registre.liste.colonnes.visePar'), dataIndex: 'viseParNomComplet', key: 'vise',
      render: (v, r) => (v
        ? <span>{v}{r.dateVisa && <span style={{ color: '#6b7280' }}>{' - '}{dayjs(r.dateVisa).format('DD/MM/YYYY')}</span>}</span>
        : <span style={{ color: '#9ca3af' }}>-</span>),
    },
    {
      /* L'ACTION ATTENDUE EST NOMMEE SUR LA LIGNE. Un simple « Ouvrir » aurait
         laisse chercher, une fois le registre ouvert, ce qu'il y a a y faire. */
      title: '', key: 'ouvrir', width: 150, align: 'right',
      render: (_, r) => (
        <Button size="small" type="primary"
          style={{ background: GREEN, borderColor: GREEN }}
          onClick={() => ouvrirRegistre(r)}>
          {actionDuRegistre(r.statut)}
        </Button>
      ),
    },
  ];

  const renderListe = () => (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <Segmented value={filtreStatut} onChange={setFiltreStatut} options={FILTRES_LISTE} />
        <Button icon={<ReloadOutlined />} onClick={chargerTousRegistres}>{t('registre.liste.actualiser')}</Button>
      </div>
      <Table
        className="cb-table"
        rowKey={(r) => r.registreTrackingId}
        size="small"
        loading={chargementListe}
        dataSource={listeAffichee}
        columns={COLONNES_LISTE}
        scroll={{ x: 900 }}
        pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (n) => t('registre.liste.total', { count: n }) }}
        locale={{ emptyText: <Empty description={t('registre.liste.aucun')} /> }}
      />
    </div>
  );

  const renderRegistre = (type) => {
    const reg = getRegistre(type);
    const lignes = getLignes(type);
    const estOuvert = reg?.statut === 'OUVERT';
    const estClos = reg?.statut === 'CLOS';

    /* AUCUN REGISTRE A CETTE DATE : on le dit. Sans ce cas, le volet montrait une
       coquille - statut « - », pas de date, aucun bouton - qu'on lisait comme une
       panne plutot que comme une absence. */
    if (!reg) {
      return (
        <Empty
          style={{ padding: '40px 0' }}
          description={(
            <span>
              <Trans t={t} i18nKey={type === 'ARRIVEE' ? 'registre.aucunRegistre.arrivee' : 'registre.aucunRegistre.depart'}
                values={{ date: dayjs(dateSelectionnee).format('DD/MM/YYYY') }}
                components={{ strong: <strong /> }} />
            </span>
          )}
        >
          {!consulteAujourdhui && (
            <Button onClick={() => setDateSelectionnee(today)}>{t('registre.date.revenirAujourdhui')}</Button>
          )}
        </Empty>
      );
    }

    return (
      <div>
        {/* ── Barre d'état ── */}
        <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Tag color={STATUT_COLOR[reg?.statut] || 'default'} style={{ fontSize: 13, padding: '4px 12px', borderRadius: 20 }}>
              {STATUT_LABEL[reg?.statut] || '-'}
            </Tag>
          </Col>
          <Col flex="auto">
            {reg?.dateJour && (
              <span style={{ color: '#555' }}>
                <Trans t={t} i18nKey="registre.barre"
                  values={{ date: dayjs(reg.dateJour).format('DD/MM/YYYY'), type: type === 'ARRIVEE' ? t('registre.types.arrivee') : t('registre.types.depart') }}
                  components={{ strong: <strong /> }} />
              </span>
            )}
          </Col>
          <Col>
            <Space>
              {/* L'export a quitte cette barre pour le bouton flottant, rendu UNE
                  SEULE FOIS hors des onglets. Rendu ici, il l'aurait ete deux fois
                  des que les deux registres ont ete ouverts - les volets visites
                  restent montes - et les deux boutons flottants se seraient
                  superposes au meme coin de l'ecran. */}
              <Button icon={<PrinterOutlined />} onClick={() => imprimer(type)}>
                {t('registre.imprimer')}
              </Button>
              {estOuvert && (
                <Popconfirm
                  title={t('registre.cloturer.titre')}
                  description={t('registre.cloturer.description')}
                  onConfirm={() => cloturer(type)}
                  okText={t('registre.cloturer.ok')}
                  okButtonProps={{ danger: true }}
                >
                  <Button icon={<LockOutlined />} danger>{t('registre.cloturer.ok')}</Button>
                </Popconfirm>
              )}
              {estClos && (
                <Button icon={<SafetyCertificateOutlined />}
                  style={{ background: GREEN, borderColor: GREEN, color: '#fff' }}
                  onClick={() => { setVisaModal(type); visaForm.resetFields(); }}>
                  {t('registre.viser')}
                </Button>
              )}
              <Button icon={<ReloadOutlined />} onClick={() => loadRegistre(type)} />
            </Space>
          </Col>
        </Row>

        {/* ── Table inline ── */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f0f9f4', borderBottom: '2px solid #d9f0e2' }}>
                <th style={thStyle(36)}>{t('registre.colonnes.numero')}</th>
                <th style={thStyle(130)}>{t('registre.colonnes.dateCorrespondance')}</th>
                <th style={thStyle(140)}>{t('registre.colonnes.numeroCorrespondance')}</th>
                <th style={thStyle(180)}>{t('registre.colonnes.expediteur')}</th>
                <th style={{ ...thStyle(), textAlign: 'left' }}>{t('registre.colonnes.objet')}</th>
                <th style={thStyle(160)}>{t('registre.colonnes.serviceDestinataire')}</th>
                {estOuvert && <th style={thStyle(110)}>{t('registre.colonnes.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {lignes.map((ligne, idx) => (
                <tr key={ligne._id}
                  style={{ borderBottom: '1px solid #f0f0f0', background: idx % 2 === 0 ? '#fff' : '#fafff9' }}>
                  <td style={{ padding: '6px', textAlign: 'center', color: '#888', fontWeight: 700 }}>
                    {idx + 1}
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    {ligne.saved ? (
                      <span>{ligne.dateCorrespondance ? dayjs(ligne.dateCorrespondance).format('DD/MM/YYYY') : '-'}</span>
                    ) : (
                      <DatePicker
                        value={ligne.dateCorrespondance}
                        onChange={v => updateLigne(type, ligne._id, { dateCorrespondance: v })}
                        format="DD/MM/YYYY"
                        size="small"
                        style={{ width: '100%' }}
                        allowClear
                      />
                    )}
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    {ligne.saved ? (
                      <span style={{ fontFamily: 'var(--font-main)' }}>{ligne.numeroCorrespondance || '-'}</span>
                    ) : (
                      <Input
                        size="small"
                        value={ligne.numeroCorrespondance}
                        onChange={e => updateLigne(type, ligne._id, { numeroCorrespondance: e.target.value })}
                        placeholder={t('registre.saisie.reference')}
                        style={{ fontFamily: 'var(--font-main)' }}
                      />
                    )}
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    {ligne.saved ? (
                      <span>{ligne.expediteur}</span>
                    ) : (
                      <Input
                        size="small"
                        value={ligne.expediteur}
                        onChange={e => updateLigne(type, ligne._id, { expediteur: e.target.value })}
                        placeholder={t('registre.saisie.expediteur')}
                      />
                    )}
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    {ligne.saved ? (
                      <span>{ligne.objet}</span>
                    ) : (
                      <Input
                        size="small"
                        value={ligne.objet}
                        onChange={e => updateLigne(type, ligne._id, { objet: e.target.value })}
                        placeholder={t('registre.saisie.objet')}
                      />
                    )}
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    {ligne.saved ? (
                      <span style={{ fontSize: 12, color: '#555' }}>{ligne.serviceDestinataire || '-'}</span>
                    ) : (
                      <Input
                        size="small"
                        value={ligne.serviceDestinataire}
                        onChange={e => updateLigne(type, ligne._id, { serviceDestinataire: e.target.value })}
                        placeholder={t('registre.saisie.service')}
                      />
                    )}
                  </td>
                  {estOuvert && (
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <Space size={4}>
                        {!ligne.saved && (
                          <Tooltip title={t('registre.saisie.enregistrerAide')}>
                            <Button size="small" type="primary"
                              style={{ background: GREEN, borderColor: GREEN }}
                              onClick={() => saveLigne(type, ligne)}>
                              {t('registre.saisie.enregistrer')}
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip title={t('registre.saisie.dupliquer')}>
                          <Button size="small" icon={<PlusOutlined />}
                            style={{ borderColor: GREEN, color: GREEN }}
                            onClick={() => dupLigne(type, idx)} />
                        </Tooltip>
                        {!ligne.saved && (
                          <Tooltip title={t('registre.saisie.supprimer')}>
                            <Button size="small" danger icon={<DeleteOutlined />}
                              onClick={() => removeLigne(type, ligne._id)} />
                          </Tooltip>
                        )}
                      </Space>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {estOuvert && (
          <Button type="dashed" icon={<PlusOutlined />}
            style={{ marginTop: 12, width: '100%', borderColor: GREEN, color: GREEN }}
            onClick={() => addLigne(type)}>
            {t('registre.saisie.ajouter')}
          </Button>
        )}

      </div>
    );
  };

  return (
    <Layout title={t('registre.titre')}>
      {/* Les deux registres sont independants et un seul est visible a la fois :
          le compte suit donc l'onglet actif, sans quoi il annoncerait des lignes
          que l'ecran n'affiche pas. */}
      <PageHeader
        titre={t('registre.titre')}
        icone={<BookOutlined />}
        compte={estVueListe ? tousRegistres.length : getLignes(activeTab).length}
        unite={estVueListe ? t('registre.uniteRegistre') : t('registre.unite')}
      />

      {/* ── La date consultee ──────────────────────────────────────────────────
          Masquee sur la vue liste, qui porte toutes les dates a la fois. Le
          bandeau d'avertissement n'apparait que hors d'aujourd'hui : sans lui, on
          saisit dans le registre d'hier en croyant tenir celui du jour, et rien a
          l'ecran ne le dementait. */}
      {!estVueListe && (
        <div style={{
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
          marginBottom: 16,
          padding: consulteAujourdhui ? 0 : '10px 14px',
          borderRadius: consulteAujourdhui ? 0 : 10,
          background: consulteAujourdhui ? 'transparent' : '#fff7ed',
          border: consulteAujourdhui ? 'none' : '1px solid #fed7aa',
        }}>
          <CalendarOutlined style={{ color: consulteAujourdhui ? '#6b7280' : '#c2410c' }} />
          <span style={{ fontSize: 13, color: consulteAujourdhui ? '#6b7280' : '#9a3412' }}>
            {consulteAujourdhui ? t('registre.date.duJour') : t('registre.date.passe')}
          </span>
          <DatePicker
            value={dayjs(dateSelectionnee)}
            onChange={(v) => setDateSelectionnee((v || dayjs()).format('YYYY-MM-DD'))}
            format="DD/MM/YYYY"
            allowClear={false}
            disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
          />
          {!consulteAujourdhui && (
            <Button size="small" onClick={() => setDateSelectionnee(today)}>
              {t('registre.date.revenirAujourdhui')}
            </Button>
          )}
        </div>
      )}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'ARRIVEE',
            label: (
              <span>
                <Badge dot={registreArrivee?.statut === 'OUVERT'} color={GREEN} offset={[4, 0]}>
                  {t('registre.onglets.arrivee')}
                </Badge>
              </span>
            ),
            children: renderRegistre('ARRIVEE'),
          },
          {
            key: 'DEPART',
            label: (
              <span>
                <Badge dot={registreDepart?.statut === 'OUVERT'} color={GREEN} offset={[4, 0]}>
                  {t('registre.onglets.depart')}
                </Badge>
              </span>
            ),
            children: renderRegistre('DEPART'),
          },
          {
            key: 'TOUS',
            label: (
              <span>
                <UnorderedListOutlined /> {t('registre.onglets.tous')}
                {aTraiter > 0 && (
                  <Badge count={aTraiter} style={{ backgroundColor: '#d97706', marginLeft: 8 }} />
                )}
              </span>
            ),
            children: renderListe(),
          },
        ]}
      />

      {/* ── Export, bouton flottant ────────────────────────────────────────────
          UNE SEULE FOIS, hors des onglets, et pilote par l'onglet actif. Rendu
          dans `renderRegistre`, il l'aurait ete deux fois des que les deux
          registres ont ete ouverts - les volets visites restent montes - et les
          deux boutons se seraient superposes au meme coin de l'ecran.

          Flottant pour la meme raison que sur « Courriers » et « Bordereaux » :
          c'est le meme ecran, et le bouton ne doit pas changer de place quand on
          passe d'un onglet a l'autre. */}
      {/* Pas sur la vue liste : elle n'a pas de lignes de registre a exporter, et un
          bouton desactive en bas a droite se lirait comme une panne. */}
      {!estVueListe && (
        <TableExportButton
          floating
          data={lignesExportables}
          columns={colonnesExport(t)}
          count={lignesExportables.length}
          filename={`registre-journalier-${activeTab.toLowerCase()}-${dateSelectionnee}`}
          title={t('registre.export.titre', { type: activeTab === 'ARRIVEE' ? t('registre.types.arrivee') : t('registre.types.depart') })}
          subtitle={t('registre.export.sousTitre', { date: dayjs(dateSelectionnee).format('DD/MM/YYYY'), statut: STATUT_LABEL[getRegistre(activeTab)?.statut] || '-' })}
        />
      )}

      {/* Modal visa */}
      <CBModal
        open={!!visaModal}
        title={t('registre.visa.titre', { type: visaModal || '' })}
        onCancel={() => setVisaModal(null)}
        onOk={() => visaForm.submit()}
        okText={t('registre.visa.ok')}
        okButtonProps={{ style: { background: GREEN, borderColor: GREEN } }}
      >
        <Form form={visaForm} layout="vertical" onFinish={viser}>
          <Form.Item label={t('registre.visa.commentaire')} name="commentaireChef">
            <Input.TextArea rows={3} placeholder={t('registre.visa.commentairePlaceholder')} />
          </Form.Item>
        </Form>
      </CBModal>
    </Layout>
  );
}

// ─── Style helpers ────────────────────────────────────────────
const thStyle = (width) => ({
  padding: '8px 6px',
  textAlign: 'center',
  color: '#555',
  fontWeight: 600,
  ...(width ? { width } : {}),
});

